import { AppSyncFarm, MonitorVaults } from 'common/models/farm';
import React from 'react';
import { ScreenUtils } from 'utils/screenUtils';
import { FarmUtils } from 'utils/farmUtils';
import { AppSyncUserInfo } from 'common/models/userInfo';
import FormatNumber from '../FormatPercent/FormatNumber';
import FormatFloat from '../FormatPercent/FormatFloat';
import { TokenUtils } from 'utils/tokenUtils';
import { TokenIcon } from '../TokenIcons/TokenIcon';
import { LinkButton } from '../LinkButton/LinkButton';
import { OptionalOutboundLink } from '../OptionalOutboundLink';
import { CryptoUtils } from 'utils/cryptoUtils';
import Image from 'common/components/Image';

type Props = {
  farm: AppSyncFarm;
  userInfoLoading: boolean;
  userInfo: AppSyncUserInfo;
  claiming: boolean;
  monitorVaults: MonitorVaults[] | [];
};

const VaultsTablePosition: React.FC<Props> = (props) => {
  const breakpoint = ScreenUtils.useBreakpoint();
  const farm = props.farm;
  const baseToken = FarmUtils.getBaseToken(farm);
  const pairedToken = FarmUtils.getRewardToken(farm);

  const farmUserInfo = TokenUtils.getFarmUserInfo(farm, props.userInfo);

  const apr =
    FarmUtils.getFeeAprByVaultAddress(props.monitorVaults, farm.lpAddress) ?? 0;

  const userAmount = FarmUtils.amountInTokens(
    farmUserInfo.totalLPUSD,
    baseToken
  );
  const userAmountDigits = Number(userAmount) >= 0.01 ? 2 : 4;

  const actionButtonCaption = farmUserInfo.totalLPUSD > 0 ? 'Manage' : 'Earn';

  const actionButtonStyleClass =
    actionButtonCaption === 'Manage'
      ? 'bg-none primary-color border-[#1DBC7D]'
      : '';

  const baseTokenDisplayName = baseToken.displayName === 'ETHC' ? 'ETH': baseToken.displayName;
  const pairedTokenDisplayName = pairedToken.displayName === 'ETHC' ? 'ETH': pairedToken.displayName;

  return (
    <tr key={`user-dep-pos-${props.farm.poolId}`} className="font-semibold">
      {/* Reward Token */}
      <td
        key={`rt-${props.farm.poolId}`}
        className="primary-text-color flex flex-col tablet:flex-row tablet:items-center justify-start gap-1 tablet:gap-3 border-none pt-6 pb-0 ml-5"
      >
        <div className="">
          <TokenIcon name={FarmUtils.getRewardToken(farm).name} height="40px" />
        </div>
        {pairedTokenDisplayName}
        <OptionalOutboundLink
          href={
            CryptoUtils.getScannerInfo(farm.chainId, farm.lpAddress, 'address')
              ?.link
          }
        >
          <Image className="icon-new-window" alt="new window icon" />
        </OptionalOutboundLink>
      </td>

      {/* ROI */}
      {[ScreenUtils.BreakpointName.XL].includes(breakpoint) && (
        <td
          key={`irr-${props.farm.poolId}`}
          className="primary-text-color border-none pt-6 pb-0"
        >
          {/* Don't show 0% IRR and IRR > 1,000% */}
          {apr > 0.01 && apr < 1000 ? (
            <FormatNumber value={apr} postfix="%" isLoading={false} />
          ) : (
            <></>
          )}
        </td>
      )}

      {/* Your Deposits */}
      <td
        key={`dep-${props.farm.poolId}`}
        className="primary-text-color border-none pt-6 pb-0"
      >
        <FormatFloat
          value={userAmount}
          postfix={` ${baseTokenDisplayName}`}
          displayDecimals={userAmountDigits}
        />
      </td>

      {/* Actions Column */}
      <td
        key={`act-${props.farm.poolId}`}
        className="text-right w-44 border-none pt-6 pb-0"
      >
        <LinkButton
          to={`/vault/token/${baseToken.address.toLowerCase()}/pairedtoken/${pairedToken.address.toLowerCase()}/tab/manage`}
          id={farm.poolId.toString()}
          className="w-28 tablet:w-40"
          styleClass={actionButtonStyleClass}
        >
          {actionButtonCaption}
        </LinkButton>
      </td>
    </tr>
  );
};

export default VaultsTablePosition;
